import React, { useState, useMemo } from "react";
import { Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { partnerLeadService } from "@/lib/services";
import { getQRSource, getUTM } from "@/lib/utm";
import { track } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PartnerForm() {
  const { t } = useLanguage();
  const lbls = t("partner.form.labels");
  const phs = t("partner.form.placeholders");
  const standardsOptions = t("partner.form.standardsOptions");
  const contactMethodOptions = t("partner.form.contactMethodOptions");
  const reachabilityOptions = t("partner.form.reachabilityOptions");
  const timeframeOptions = t("partner.form.timeframeOptions");
  const roleOptions = t("partner.form.roleOptions");
  const v = t("partner.form.validation");

  const [form, setForm] = useState({
    restaurant_name: "",
    contact_name: "",
    email: "",
    phone: "",
    city: "",
    dietary_standards: [],
    preferred_contact_method: "",
    best_reachability: [],
    preferred_call_timeframe: "",
    website_or_instagram: "",
    role: "",
    message: "",
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle");
  const [started, setStarted] = useState(false);

  const phoneRequired = useMemo(
    () => form.preferred_contact_method === "phone" || form.preferred_contact_method === "whatsapp",
    [form.preferred_contact_method]
  );

  const change = (field) => (ev) => {
    if (!started) {
      setStarted(true);
      track("partner_form_start");
    }
    setForm((f) => ({ ...f, [field]: ev.target.value }));
  };

  const blur = (field) => () => {
    setTouched((tt) => ({ ...tt, [field]: true }));
    const val = form[field];
    const has = Array.isArray(val) ? val.length > 0 : !!val;
    if (has) track("partner_form_field_complete", { field_name: field });
  };

  const toggleArray = (field, value) => {
    if (!started) {
      setStarted(true);
      track("partner_form_start");
    }
    setForm((f) => {
      const arr = f[field];
      const has = arr.includes(value);
      return { ...f, [field]: has ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const errors = {
    restaurant_name: !form.restaurant_name.trim() ? v.required : null,
    contact_name: !form.contact_name.trim() ? v.required : null,
    email: !form.email.trim() ? v.required : !EMAIL_RE.test(form.email) ? v.email : null,
    city: !form.city.trim() ? v.required : null,
    dietary_standards: form.dietary_standards.length === 0 ? v.standardsMin : null,
    preferred_contact_method: !form.preferred_contact_method ? v.contactMethod : null,
    best_reachability: form.best_reachability.length === 0 ? v.reachabilityMin : null,
    phone: phoneRequired && !form.phone.trim() ? v.phoneRequired : null,
  };
  const isValid = Object.values(errors).every((e) => !e);

  const submit = async (ev) => {
    ev.preventDefault();
    setTouched({
      restaurant_name: true, contact_name: true, email: true, city: true,
      dietary_standards: true, preferred_contact_method: true,
      best_reachability: true, phone: true,
    });
    track("partner_form_submit_attempt", { valid: isValid });
    if (!isValid) return;

    setSubmitting(true);
    const utm = getUTM();
    const qr = getQRSource();
    try {
      await partnerLeadService.createLead({
        ...form,
        utm_source: utm.utm_source || undefined,
        utm_medium: utm.utm_medium || undefined,
        utm_campaign: utm.utm_campaign || undefined,
        qr_source: qr.qr_source || undefined,
        campaign_source: qr.source || undefined,
      });
      setStatus("success");
      track("partner_form_submit_success", {
        city: form.city,
        dietary_standards: form.dietary_standards,
        preferred_contact_method: form.preferred_contact_method,
        source: qr.source,
      });
    } catch (e) {
      setStatus("error");
      track("partner_form_submit_error", { error_type: "network" });
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "success") {
    return (
      <div className="card-premium text-center py-16" role="status" aria-live="polite" data-testid="partner-form-success">
        <div className="mx-auto w-14 h-14 rounded-full bg-olive-faint flex items-center justify-center text-accent mb-5">
          <Check size={28} />
        </div>
        <div className="display-md text-primary mb-3">{t("partner.form.successTitle")}</div>
        <div className="body-md max-w-md mx-auto">{t("partner.form.successBody")}</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-premium" data-testid="partner-form" noValidate>
      <div className="grid md:grid-cols-2 gap-5">
        {/* restaurant_name */}
        <div className="md:col-span-2">
          <label htmlFor="restaurant_name" className="form-label">{lbls.restaurant_name}</label>
          <input id="restaurant_name" type="text" value={form.restaurant_name}
            onChange={change("restaurant_name")} onBlur={blur("restaurant_name")}
            placeholder={phs.restaurant_name} data-testid="partner-field-restaurant_name"
            className={`field-input ${touched.restaurant_name && errors.restaurant_name ? "error" : ""}`} required />
        </div>

        <div>
          <label htmlFor="contact_name" className="form-label">{lbls.contact_name}</label>
          <input id="contact_name" type="text" value={form.contact_name}
            onChange={change("contact_name")} onBlur={blur("contact_name")}
            placeholder={phs.contact_name} data-testid="partner-field-contact_name"
            className={`field-input ${touched.contact_name && errors.contact_name ? "error" : ""}`} required />
        </div>

        <div>
          <label htmlFor="role" className="form-label">{lbls.role}</label>
          <select id="role" value={form.role} onChange={change("role")}
            data-testid="partner-field-role" className="field-input">
            <option value="">{phs.role}</option>
            {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="email" className="form-label">{lbls.email}</label>
          <input id="email" type="email" value={form.email}
            onChange={change("email")} onBlur={blur("email")}
            placeholder={phs.email} data-testid="partner-field-email"
            className={`field-input ${touched.email && errors.email ? "error" : ""}`} required />
        </div>

        <div>
          <label htmlFor="phone" className="form-label">{phoneRequired ? lbls.phone_required : lbls.phone}</label>
          <input id="phone" type="tel" value={form.phone}
            onChange={change("phone")} onBlur={blur("phone")}
            placeholder={phs.phone} data-testid="partner-field-phone"
            className={`field-input ${touched.phone && errors.phone ? "error" : ""}`} />
          {touched.phone && errors.phone && (
            <div className="text-[12px] text-[#C0392B] mt-1.5">{errors.phone}</div>
          )}
        </div>

        <div>
          <label htmlFor="city" className="form-label">{lbls.city}</label>
          <input id="city" type="text" value={form.city}
            onChange={change("city")} onBlur={blur("city")}
            placeholder={phs.city} data-testid="partner-field-city"
            className={`field-input ${touched.city && errors.city ? "error" : ""}`} required />
        </div>

        <div>
          <label htmlFor="website_or_instagram" className="form-label">{lbls.website_or_instagram}</label>
          <input id="website_or_instagram" type="text" value={form.website_or_instagram}
            onChange={change("website_or_instagram")}
            placeholder={phs.website_or_instagram} data-testid="partner-field-website_or_instagram"
            className="field-input" />
        </div>

        {/* Preferred contact method */}
        <div className="md:col-span-2">
          <label className="form-label">{lbls.preferred_contact_method}</label>
          <div className="flex flex-wrap gap-2" data-testid="partner-field-preferred_contact_method">
            {contactMethodOptions.map((o) => (
              <button type="button" key={o.value}
                onClick={() => {
                  if (!started) { setStarted(true); track("partner_form_start"); }
                  setForm((f) => ({ ...f, preferred_contact_method: o.value }));
                  setTouched((tt) => ({ ...tt, preferred_contact_method: true }));
                }}
                data-testid={`partner-contact-${o.value}`}
                className={`tag-chip ${form.preferred_contact_method === o.value ? "active" : ""}`}>
                {o.label}
              </button>
            ))}
          </div>
          {touched.preferred_contact_method && errors.preferred_contact_method && (
            <div className="text-[12px] text-[#C0392B] mt-1.5">{errors.preferred_contact_method}</div>
          )}
        </div>

        {/* Best reachability */}
        <div className="md:col-span-2">
          <label className="form-label">{lbls.best_reachability}</label>
          <div className="flex flex-wrap gap-2" data-testid="partner-field-best_reachability">
            {reachabilityOptions.map((r) => {
              const active = form.best_reachability.includes(r);
              return (
                <button type="button" key={r}
                  onClick={() => toggleArray("best_reachability", r)}
                  data-testid={`partner-reach-${r}`}
                  className={`tag-chip ${active ? "active" : ""}`}>
                  {r}
                </button>
              );
            })}
          </div>
          {touched.best_reachability && errors.best_reachability && (
            <div className="text-[12px] text-[#C0392B] mt-1.5">{errors.best_reachability}</div>
          )}
        </div>

        {/* Preferred call timeframe */}
        <div className="md:col-span-2">
          <label htmlFor="preferred_call_timeframe" className="form-label">{lbls.preferred_call_timeframe}</label>
          <select id="preferred_call_timeframe" value={form.preferred_call_timeframe}
            onChange={change("preferred_call_timeframe")}
            data-testid="partner-field-preferred_call_timeframe" className="field-input">
            <option value="">{phs.preferred_call_timeframe}</option>
            {timeframeOptions.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
          </select>
        </div>

        {/* Dietary / quality standards */}
        <div className="md:col-span-2">
          <label className="form-label">{lbls.dietary_standards}</label>
          <div className="flex flex-wrap gap-2" data-testid="partner-field-dietary_standards">
            {standardsOptions.map((s) => {
              const active = form.dietary_standards.includes(s);
              return (
                <button type="button" key={s}
                  onClick={() => toggleArray("dietary_standards", s)}
                  className={`tag-chip ${active ? "active" : ""}`}
                  data-testid={`partner-standard-${s}`}>
                  {s}
                </button>
              );
            })}
          </div>
          {touched.dietary_standards && errors.dietary_standards && (
            <div className="text-[12px] text-[#C0392B] mt-1.5">{errors.dietary_standards}</div>
          )}
        </div>

        {/* Message */}
        <div className="md:col-span-2">
          <label htmlFor="message" className="form-label">{lbls.message}</label>
          <textarea id="message" value={form.message} onChange={change("message")}
            placeholder={phs.message} data-testid="partner-field-message"
            className="field-textarea" maxLength={1000} />
        </div>
      </div>

      <button type="submit" disabled={!isValid || submitting}
        data-testid="partner-form-submit"
        className="btn-primary w-full justify-center mt-8">
        {submitting ? "..." : t("partner.form.submit")}
      </button>
      <div className="text-center body-sm mt-4">{t("partner.form.belowSubmit")}</div>

      {status === "error" && (
        <div className="text-center body-sm mt-3 text-[#C0392B]" role="alert" data-testid="partner-form-error">
          {t("partner.form.error")}
        </div>
      )}
    </form>
  );
}
